import React from "react";
import { Link } from "react-router-dom";
import { Award, Shield, Users, Factory, ArrowRight } from "lucide-react";

export default function About() {
  return (
    <main data-testid="about-page">
      <section className="bg-neutral-50 py-14 md:py-28">
        <div className="max-w-[1100px] mx-auto px-4 md:px-8 text-center">
          <span className="text-[10px] md:text-[11px] font-bold tracking-[0.26em] uppercase text-[#FF5A1F]">Since 2017</span>
          <h1 className="mt-3 md:mt-4 font-display text-4xl sm:text-5xl md:text-7xl font-black leading-[1] md:leading-[0.98]">
            Designed in India,<br /> carried everywhere.
          </h1>
          <p className="mt-4 md:mt-6 text-sm md:text-lg text-neutral-600 max-w-2xl mx-auto leading-relaxed">
            Stealodeal was born from a simple belief: your everyday essentials should be smarter,
            slimmer and secure. We craft RFID-protected card holders, premium wallets and
            multi-tool keychains — all engineered to live in your pocket and outlast trends.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 grid md:grid-cols-2 gap-12 items-center">
          <div className="aspect-[4/5] rounded-3xl overflow-hidden bg-neutral-100">
            <img
              src="https://m.media-amazon.com/images/I/718L9cT17oL._SL1100_.jpg"
              alt="Stealodeal craft"
              className="w-full h-full object-contain p-10"
            />
          </div>
          <div>
            <h2 className="font-display text-4xl md:text-5xl font-black leading-tight">
              Built by <span className="text-[#FF5A1F]">Devan & Co.</span>
            </h2>
            <p className="mt-5 text-neutral-700 leading-relaxed">
              What started as one product — a slim RFID card holder — grew into a family of
              essentials loved by 10,000+ customers across India. Every piece is tested for
              real life: the morning commute, the evening rush, the weekend trail.
            </p>
            <p className="mt-4 text-neutral-700 leading-relaxed">
              We obsess over materials — aviation-grade aluminium, stainless steel,
              genuine PU leatherite — so you don't have to compromise on form or function.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-6">
              <Stat value="10K+" label="Happy customers" />
              <Stat value="8 yrs" label="On Amazon" />
              <Stat value="85%" label="Positive ratings" />
              <Stat value="Made" label="In India" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-neutral-50">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8">
          <h2 className="font-display text-4xl md:text-5xl font-black text-center">What we stand for</h2>
          <div className="mt-12 grid md:grid-cols-4 gap-6">
            {[
              { icon: Shield, t: "Security first", d: "Every wallet ships RFID-protected. Your cards, your data — yours alone." },
              { icon: Award, t: "Built to last", d: "6-month warranty on every piece. Materials tested for daily wear." },
              { icon: Users, t: "Customer obsessed", d: "85% positive ratings from 10,000+ customers over 8 years." },
              { icon: Factory, t: "Made in India", d: "Designed and assembled by Indian artisans in New Delhi." },
            ].map((v) => (
              <div key={v.t} className="bg-white p-7 rounded-3xl">
                <div className="w-11 h-11 rounded-full bg-[#FF5A1F]/10 text-[#FF5A1F] flex items-center justify-center">
                  <v.icon className="w-5 h-5" />
                </div>
                <h3 className="mt-5 font-display text-xl font-black">{v.t}</h3>
                <p className="mt-2 text-sm text-neutral-600">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-[900px] mx-auto px-4 text-center">
          <h3 className="font-display text-3xl md:text-4xl font-black">Ready to carry smarter?</h3>
          <Link
            to="/shop"
            className="mt-6 inline-flex items-center gap-2 h-12 px-7 rounded-full bg-[#FF5A1F] hover:bg-neutral-900 text-white font-semibold text-sm transition"
          >
            Shop All Essentials <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </main>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <div className="font-display text-3xl md:text-4xl font-black text-[#FF5A1F]">{value}</div>
      <div className="text-sm text-neutral-500 mt-1">{label}</div>
    </div>
  );
}
